import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Edit2, ChevronRight, TrendingUp, Award } from "lucide-react";
import ClientBackground from "../components/ClientBackground";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

export default function ChildProfile() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [childRes, milestonesRes] = await Promise.all([
          api.get(`/children/profiles/${childId}/`),
          api.get(`/children/milestones/?child=${childId}`)
        ]);
        setChild(childRes.data);
        setMilestones(milestonesRes.data);
        console.log("Milestones data:", milestonesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [childId]);

  if (loading) {
    return <LoadingSpinner fullScreen={true} />;
  }

  if (!child) {
    return <div>Child not found</div>;
  }

  const actionCards = [
    { title: "Weekly\nChecklist", color: "from-[#7FE5D3] to-[#5FD3C1]", path: `/weekly-checklist/${childId}` },
    { title: "Recommended\nActivities", color: "from-[#FDB5C8] to-[#FD9AB5]", path: `/recommended-activities/${childId}` },
    { title: "Track Progress", color: "from-[#E8F5B2] to-[#D5E89F]", path: `/track-progress/${childId}` },
    { title: "Development Milestones", color: "from-[#B8C5FF] to-[#A0B0FF]", path: `/child/${childId}/milestones-status` },
    { title: "Alerts &\nReminders", color: "from-[#FFB88C] to-[#FFA474]", path: `/alerts-reminders/${childId}` },
    { title: "Question &\nAnswer", color: "from-[#FFE66D] to-[#FFD93D]", path: `/question-answer/${childId}` },
    { title: "Media Analysis", color: "from-[#FFE66D] to-[#FFD474]", path: `/media-analysis/${childId}` }
  ];

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10">
        <div className="bg-white/0 backdrop-blur-sm px-5 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/client-dashboard")} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">Child's Profile</h1>
          <div className="w-10" />
        </div>

        {/* Main content container with max-width */}
        <div className="px-5 py-6 mt-10 max-w-6xl mx-auto">
          {/* Profile card with max-width */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="relative bg-gradient-to-br from-[#FDB913] to-[#FFA500] rounded-3xl p-6 shadow-lg">
              <button
                onClick={() => navigate(`/edit-child-profile/${childId}`)}
                className="absolute top-4 right-4 bg-white/90 rounded-full p-2 shadow-md"
              >
                <Edit2 className="w-4 h-4 text-gray-700" />
              </button>

              <div className="flex flex-col items-center pt-8">
                <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-white shadow-xl mb-4">
                  <img
                    src={child.profile_image}
                    alt={child.first_name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <h2 className="text-2xl font-bold text-white mb-6">
                  {child.first_name} {child.last_name}
                </h2>

                <div className="flex gap-4 w-full max-w-sm">
                  <div className="flex-1 bg-white/95 rounded-2xl p-4 text-center">
                    <div className="text-xs text-gray-600 mb-1">Weight</div>
                    <div className="text-lg font-bold text-[#1e3a5f]">
                      {child.weight_kg || "N/A"}
                    </div>
                    <div className="text-xs text-gray-500">in Kg</div>
                  </div>

                  <div className="flex-1 bg-white/95 rounded-2xl p-4 text-center">
                    <div className="text-xs text-gray-600 mb-1">Age</div>
                    <div className="text-lg font-bold text-[#1e3a5f]">
                      {child.age_in_months
                        ? `${Math.floor(child.age_in_months / 12)} years ${child.age_in_months % 12} months`
                        : "N/A"}
                    </div>
                  </div>

                  <div className="flex-1 bg-white/95 rounded-2xl p-4 text-center">
                    <div className="text-xs text-gray-600 mb-1">Height</div>
                    <div className="text-lg font-bold text-[#1e3a5f]">
                      {child.height_cm || "N/A"}
                    </div>
                    <div className="text-xs text-gray-500">in Cm</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Milestones section with max-width */}
          <div className="max-w-4xl mx-auto mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1e3a5f]">
                {child.first_name}'s Milestones 🏆
              </h3>
            </div>

            <div className="space-y-4">
              {milestones.length > 0 ? (
                milestones.map((milestone) => {
                  return (
                    <div
                      key={milestone.id}
                      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-800 text-base">{milestone.title}</h4>
                        <span className="text-xs font-bold text-white bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] px-3 py-1 rounded-full shadow-sm">
                          {milestone.completion_percentage || 0}%
                        </span>
                      </div>

                      <div className="relative w-full h-4 rounded-full overflow-visible">
                        <div className="absolute inset-0 flex rounded-full overflow-hidden">
                          <div className="h-full bg-[#1e3a5f]" style={{ width: '33.33%' }}></div>
                          <div className="h-full bg-[#7DD3F8]" style={{ width: '33.33%' }}></div>
                          <div className="h-full bg-[#FFB6C1]" style={{ width: '33.34%' }}></div>
                        </div>

                        <div
                          className="absolute top-1/2 -translate-y-1/2 transition-all duration-500 ease-out"
                          style={{ left: `${milestone.completion_percentage || 0}%` }}
                        >
                          <div className="relative -translate-x-1/2">
                            <div className="absolute inset-0 w-8 h-8 bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] rounded-full opacity-20 blur-sm -inset-1"></div>
                            <div className="relative w-7 h-7 bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] rounded-full shadow-2xl border-2 border-white">
                              <div className="absolute top-0.5 left-0.5 w-2 h-2 bg-white/40 rounded-full blur-[1px]"></div>
                            </div>
                            <div className="absolute inset-0 w-7 h-7 bg-[#1e3a5f] rounded-full animate-ping opacity-30"></div>
                          </div>
                        </div>
                      </div>

                      {milestone.completion_percentage >= 75 && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-green-600">
                          <TrendingUp className="w-4 h-4" />
                          <span className="font-medium">Excellent Progress!</span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-2xl p-6 text-center text-gray-500 shadow-sm">
                  <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No milestones tracked yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Action cards grid with responsive max-width */}
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-20">
              {actionCards.map((card, index) => (
                <button
                  key={index}
                  onClick={() => navigate(card.path)}
                  className="group relative bg-white rounded-3xl p-6 shadow-md hover:shadow-xl active:scale-[0.98] transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 max-w-[280px] w-full mx-auto"
                >
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.color} rounded-t-3xl`}></div>
                  <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                  
                  <div className="relative z-10">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${card.color} shadow-md mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      {index === 0 && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                      )}
                      {index === 1 && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )}
                      {index === 2 && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      )}
                      {index === 3 && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                      )}
                      {index === 4 && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                      )}
                      {index === 5 && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      )}
                      {index === 6 && (
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.546-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>

                    <h4 className="text-[#1e3a5f] font-bold text-left whitespace-pre-line text-base leading-tight mb-3">
                      {card.title}
                    </h4>

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <span className="text-gray-500 text-xs font-medium group-hover:text-[#1e3a5f] transition-colors">
                        View details
                      </span>
                      <div className="bg-gray-100 rounded-full p-1.5 group-hover:bg-gray-200 transition-all duration-300">
                        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#1e3a5f] group-hover:translate-x-0.5 transition-all" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
